import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "./i18n/LanguageContext";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { ChatWidget } from "./components/ChatWidget";
import { CookieConsent } from "./components/CookieConsent";
import { VisitorPulse } from "./lib/VisitorPulse";
import { BackToTop } from "./components/BackToTop";
import { ScrollReveal } from "./components/ScrollReveal";
import { Home } from "./pages/Home";

const Services = lazy(() => import("./pages/Services").then(m => ({ default: m.Services })));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail").then(m => ({ default: m.ServiceDetail })));
const Products = lazy(() => import("./pages/Products").then(m => ({ default: m.Products })));
const ProductDetail = lazy(() => import("./pages/ProductDetail").then(m => ({ default: m.ProductDetail })));
const Solutions = lazy(() => import("./pages/Solutions").then(m => ({ default: m.Solutions })));
const SolutionDetail = lazy(() => import("./pages/SolutionDetail").then(m => ({ default: m.SolutionDetail })));
const SovereignCloud = lazy(() => import("./pages/SovereignCloud").then(m => ({ default: m.SovereignCloud })));
const Regions = lazy(() => import("./pages/Regions").then(m => ({ default: m.Regions })));
const Trust = lazy(() => import("./pages/Trust").then(m => ({ default: m.Trust })));
const Security = lazy(() => import("./pages/Security").then(m => ({ default: m.Security })));
const Pricing = lazy(() => import("./pages/Pricing").then(m => ({ default: m.Pricing })));
const CloudAccess = lazy(() => import("./pages/CloudAccess").then(m => ({ default: m.CloudAccess })));
const Support = lazy(() => import("./pages/Support").then(m => ({ default: m.Support })));
const Status = lazy(() => import("./pages/Status").then(m => ({ default: m.Status })));
const Console = lazy(() => import("./pages/Console").then(m => ({ default: m.Console })));
const Legal = lazy(() => import("./pages/Legal").then(m => ({ default: m.Legal })));
const Docs = lazy(() => import("./pages/Docs").then(m => ({ default: m.Docs })));
const Resources = lazy(() => import("./pages/Resources").then(m => ({ default: m.Resources })));
const ResourceDetail = lazy(() => import("./pages/ResourceDetail").then(m => ({ default: m.ResourceDetail })));
const About = lazy(() => import("./pages/About").then(m => ({ default: m.About })));
const Contact = lazy(() => import("./pages/Contact").then(m => ({ default: m.Contact })));
const Developers = lazy(() => import("./pages/Developers").then(m => ({ default: m.Developers })));
const WhyDaakyi = lazy(() => import("./pages/WhyDaakyi").then(m => ({ default: m.WhyDaakyi })));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Migrate = lazy(() => import("./pages/Migrate"));
const Platform = lazy(() => import("./pages/Platform").then(m => ({ default: m.Platform })));
const Fr = lazy(() => import("./pages/Fr"));
const OpsConsole = lazy(() => import("./pages/OpsConsole"));
const PitchDeck = lazy(() => import("./pages/PitchDeck"));
const Insights = lazy(() => import("./pages/Insights"));
const InsightsArticle = lazy(() => import("./pages/InsightsArticle"));
const CloudIndex = lazy(() => import("./pages/CloudIndex"));
const CloudCountry = lazy(() => import("./pages/CloudCountry"));
const CompareIndex = lazy(() => import("./pages/CompareIndex"));
const ComparePage = lazy(() => import("./pages/ComparePage"));

const isSnap = typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";

function UtmTracker() {
  useEffect(() => {
    if (isSnap) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const source = params.get('utm_source');
      if (!source || sessionStorage.getItem('daakyi_utm_tracked')) return;
      sessionStorage.setItem('daakyi_utm_tracked', '1');
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/utm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source,
          medium: params.get('utm_medium') || '',
          campaign: params.get('utm_campaign') || '',
          path: window.location.pathname
        })
      }).catch(() => {});
    } catch { /* private mode */ }
  }, []);
  return null;
}

let lastTracked = { path: '', at: 0 };

function PageViewTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (isSnap || pathname.startsWith('/ops-console')) return;
    const now = Date.now();
    if (lastTracked.path === pathname && now - lastTracked.at < 5000) return;
    lastTracked = { path: pathname, at: now };
    let ref = '';
    try {
      if (!sessionStorage.getItem('daakyi_pv_session')) {
        sessionStorage.setItem('daakyi_pv_session', '1');
        ref = document.referrer || '';
      }
    } catch { /* private mode */ }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({ path: pathname, ref })
    }).catch(() => {});
  }, [pathname]);
  return null;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        else window.scrollTo(0, 0);
      }, 100);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center" data-testid="page-loader">
    <div className="w-10 h-10 rounded-full border-2 border-[#E2E8F0] border-t-[#F29B3D] animate-spin" />
  </div>
);

const HideOnOps = ({ children }) => {
  const location = useLocation();
  if (location.pathname.startsWith('/ops-console') || location.pathname.startsWith('/deck/')) return null;
  return children;
};

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
      <div className="App min-h-screen flex flex-col">
        <BrowserRouter>
          <ScrollToTop />
          <UtmTracker />
          <PageViewTracker />
          {!isSnap && <VisitorPulse />}
          <HideOnOps><Header /></HideOnOps>
          <div className="flex-1">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/platform" element={<Platform />} />
                <Route path="/fr" element={<Fr />} />
                <Route path="/ops-console" element={<OpsConsole />} />
                <Route path="/deck/:token" element={<PitchDeck />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/:productId" element={<ProductDetail />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/:serviceId" element={<ServiceDetail />} />
                <Route path="/solutions" element={<Solutions />} />
                <Route path="/solutions/sovereign-cloud" element={<SovereignCloud />} />
                <Route path="/solutions/:industryId" element={<SolutionDetail />} />
                <Route path="/regions" element={<Regions />} />
                <Route path="/insights" element={<Insights />} />
                <Route path="/insights/:slug" element={<InsightsArticle />} />
                <Route path="/cloud" element={<CloudIndex />} />
                <Route path="/cloud/:slug" element={<CloudCountry />} />
                <Route path="/compare" element={<CompareIndex />} />
                <Route path="/compare/:slug" element={<ComparePage />} />
                <Route path="/trust" element={<Trust />} />
                <Route path="/security" element={<Security />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/pricing/calculator" element={<Navigate to="/pricing" replace />} />
                <Route path="/pricing/services" element={<Navigate to="/pricing" replace />} />
                <Route path="/cloud-access" element={<CloudAccess />} />
                <Route path="/support" element={<Support />} />
                <Route path="/status" element={<Status />} />
                <Route path="/console" element={<Console />} />
                <Route path="/legal" element={<Legal />} />
                <Route path="/docs" element={<Docs />} />
                <Route path="/docs/:category/:articleId" element={<Docs />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/resources/:resourceId" element={<ResourceDetail />} />
                <Route path="/about" element={<About />} />
                <Route path="/why-daakyi" element={<WhyDaakyi />} />
                <Route path="/developers" element={<Developers />} />
                <Route path="/migrate" element={<Migrate />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </div>
          <HideOnOps><Footer /></HideOnOps>
          {!isSnap && <ScrollReveal />}
          {!isSnap && <BackToTop />}
          {!isSnap && <HideOnOps><ChatWidget /></HideOnOps>}
          {!isSnap && <HideOnOps><CookieConsent /></HideOnOps>}
        </BrowserRouter>
      </div>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
